import { AxiosError } from 'axios'
import { PrintLabelFormat, QZHIDDevice, TestPrintType } from '../printing/types'

import { FeatureFlagState } from '../utils/featureFlipper'
/* eslint-disable no-shadow, @typescript-eslint/no-shadow  */
export enum CurrentActions {
  SetCurrentHIDDevice = 'SET_CURRENT_HID_DEVICE',
  SetCurrentPrinter = 'SET_CURRENT_PRINTER',
  SetCurrentPrinterType = 'SET_CURRENT_PRINTER_TYPE',
  SetTestPrintType = 'SET_TEST_PRINT_TYPE',
  EstablishQZConnection = 'ESTABLISH_QZ_CONNECTION',
  EstablishQZConnectionResult = 'ESTABLISH_QZ_CONNECTION_RESULT',

  SetProductLabelPrintSettings = 'SET_PRODUCT_LABEL_PRINT_SETTINGS',
  SetOrganizationSettings = 'SET_ORGANIZATION_SETTINGS',

  RequestSetOrganizationSettings = 'REQUEST_SET_ORGANIZATION_SETTINGS',
  RequestSetOrganizationSettingsSuccess = 'REQUEST_SET_ORGANIZATION_SETTINGS_SUCCESS',
  RequestSetOrganizationSettingsError = 'REQUEST_SET_ORGANIZATION_SETTINGS_ERROR',

  RequestOrganizationSettings = 'REQUEST_ORGANIZATION_SETTINGS',
  RequestOrganizationSettingsSuccess = 'REQUEST_ORGANIZATION_SETTINGS_SUCCESS',
  RequestOrganizationSettingsError = 'REQUEST_ORGANIZATION_SETTINGS_ERROR',

  RequestInventoryLocations = 'REQUEST_INVENTORY_LOCATIONS',
  RequestInventoryLocationsSuccess = 'REQUEST_INVENTORY_LOCATIONS_SUCCESS',
  RequestInventoryLocationsError = 'REQUEST_INVENTORY_LOCATIONS_ERROR',

  SetFeature = 'SET_FEATURE'
}

export interface SetCurrentHIDDeviceAction {
  type: typeof CurrentActions.SetCurrentHIDDevice
  HIDDevice: QZHIDDevice
}

export interface SetTestPrintTypeAction {
  type: typeof CurrentActions.SetTestPrintType
  testPrintType: TestPrintType
}

export interface SetCurrentPrinterAction {
  type: typeof CurrentActions.SetCurrentPrinter
  printer: string
}
export interface SetCurrentPrinterTypeAction {
  type: typeof CurrentActions.SetCurrentPrinterType
  format: PrintLabelFormat
}
export interface EstablishQZConnectionAction {
  type: typeof CurrentActions.EstablishQZConnection
}
export interface EstablishQZConnectionResultAction {
  type: typeof CurrentActions.EstablishQZConnectionResult
  connected: boolean
}
export interface SetProductLabelPrintSettingsAction {
  type: typeof CurrentActions.SetProductLabelPrintSettings
  key: 'autoPrint' | 'inventoryLocation' | 'title' | 'supplier'
  value: any
}
export interface SetOrganizationSettingsAction {
  type: typeof CurrentActions.SetOrganizationSettings
  key: keyof OrganizationSettings
  value: any
}

export interface RequestSetOrganizationSettings {
  type: typeof CurrentActions.RequestSetOrganizationSettings
}
export interface RequestSetOrganizationSettingsSuccess {
  type: typeof CurrentActions.RequestSetOrganizationSettingsSuccess
  payload: object
}
export interface RequestSetOrganizationSettingsError {
  type: typeof CurrentActions.RequestSetOrganizationSettingsError
  error: Error
}

export interface RequestOrganizationSettings {
  type: typeof CurrentActions.RequestOrganizationSettings
}
export interface RequestOrganizationSettingsSuccess {
  type: typeof CurrentActions.RequestOrganizationSettingsSuccess
  payload: object
}
export interface RequestOrganizationSettingsError {
  type: typeof CurrentActions.RequestOrganizationSettingsError
  error: Error
}

export interface RequestInventoryLocations {
  type: typeof CurrentActions.RequestInventoryLocations
}
export interface RequestInventoryLocationsSuccess {
  type: typeof CurrentActions.RequestInventoryLocationsSuccess
  values: SelectOption[]
}
export interface RequestInventoryLocationsError {
  type: typeof CurrentActions.RequestInventoryLocationsError
  error: AxiosError
}

export interface SetFeature {
  type: typeof CurrentActions.SetFeature
  key: string
  value: FeatureFlagState
}

export interface LaunchDarklySettings {
  key: number
  anonymous: boolean
  email: string
  name: string
  groups: string[]
  custom: {
    groups: string[]
  }
}

export interface PrintSettings {
  productLabelName: boolean
  productLabelCurrentDate: boolean
  productLabelSupplierName: boolean
  productLabelInventoryLocationName: boolean
}

export interface OrganizationSettings {
  hubspotEnabled: boolean
  labelPrintType: string
  printingEnabled: boolean
  printSettings: PrintSettings
  uspsEnabled: boolean
  listerConnectEnabled: boolean
  loading: boolean
  entrupyEnabled: boolean
  hammoqEnabled: boolean
  carrierId: number
  watermarkImageSet: boolean
  pearldiveEnabled: boolean
}

/* eslint-disable no-shadow, @typescript-eslint/no-shadow */
export enum Incoterm {
  /**
   * DDP (Deliveries Duty Paid) means the seller pays duty fees (taxes)
   * This is the default
   */
  DDP = 'DDP',
  /**
   * DAP (Duties At Place) means the customer pays duty fees (taxes)
   */
  DAP = 'DAP'
}
export interface OrganizationShippingSettings {
  autoSelectBestRate: boolean
  requirePacking: boolean
  disabledCarrierRates: string[]
  signatureRequiredAmount?: string
  defaultIncoterm: Incoterm
  shippingBoxPaddingInches: string
  insuranceThreshold: string
  paddingChoices: ShippingBoxPaddingChoice[]
  insureLabelCost: boolean
  autoSelectPacker: boolean
}

export interface ShippingBoxPaddingChoice {
  name: string
  value: string // string b/c we use this in a NumericInput controlled component
  key?: number // the backend doesn't use this field. The frontend does for state management.
  default: boolean
}

// Like "ConnectedMarket", but only include each market once. Only the market.
export interface UniqueConnectedMarket {
  id: number
  name: string
  humanReadableName: string
  onboardedSummary: 'NO' | 'PARTIAL' | 'YES'
}

export interface ConnectedMarket {
  fullHumanReadableName: string
  humanReadableName: string
  marketId: number
  marketAccountId: number
  /** @deprecated This object grew into something sloppy. We defined fields as if it's a Market.
   * But it's really a MarketAccount - there will be one of these objects for each MarketAccount the org has.
   * Referencing this ID field in consuming code is misleading.
   * For clarity and ease of future refactoring, please consider whether your consuming code needs a Market or a MarketAccount,
   * then use marketId or marketAccountId instead. */
  id: number
  name: string
  onboarded: boolean
  shouldRelink: boolean
  username?: string
  label?: string
}

export interface ConnectedMarketAccount {
  fullHumanReadableName: string
  humanReadableName: string
  marketId: number
  id: number
  name: string
  onboarded: boolean
  shouldRelink: boolean
  username?: string
  label?: string
}

export interface EnabledMarkets {
  shopgoodwill: boolean
  ebay: boolean
  offerUp: boolean
  letgo: boolean
  facebook: boolean
  goodwillfinds: boolean
}

export interface OrganizationAddress {
  city: string
  country: string
  name: string
  phone: string
  state: string
  street1: string
  street2: string
  zip: string
}

export interface Organization {
  address: OrganizationAddress
  addressCity: string
  addressState: string
  addressStreet: string
  addressZipcode: string
  /** @deprecated Prefer to use connectedMarketAccounts. If you need to exclude bluebox, filter by `listable` */
  connectedMarkets: ConnectedMarket[]
  connectedMarketAccounts: ConnectedMarketAccount[]
  uniqueConnectedMarkets: UniqueConnectedMarket[]
  createdAt: string
  email: string
  enabledMarkets: EnabledMarkets
  id: number
  name: string
  phone: string
  productFormRequiredFields: string[]
  settings: OrganizationSettings
  shippingSettings: OrganizationShippingSettings
  timezone: string
}

// This type was generated by `rails runner ./scripts/roles/generate_permission_types.rb`
export type UserPermission =
  | 'products.view'
  | 'products.post'
  | 'products.inventory_management'
  | 'products.isbn_scanning'
  | 'listings.view'
  | 'listings.manage'
  | 'manifests.edit'
  | 'manifests.process'
  | 'manifests.delete'
  | 'manifests.pickup'
  | 'manifests.override_state'
  | 'manifests.edit_process'
  | 'orders.all'
  | 'orders.picking'
  | 'shipping.all'
  | 'settings.general'
  | 'settings.authentications'
  | 'settings.categories'
  | 'settings.channels'
  | 'settings.watermark'
  | 'settings.developer'
  | 'settings.inventory_locations'
  | 'settings.strategies'
  | 'settings.manifests'
  | 'settings.notifications'
  | 'settings.orders'
  | 'settings.printing'
  | 'settings.products'
  | 'settings.shipping_boxes'
  | 'settings.shipping'
  | 'settings.suppliers'
  | 'settings.isbn_books_and_disc_media'
  | 'settings.team'
  | 'settings.templates'
  | 'settings.embedded_listings'
  | 'settings.sqs_feed'
  | 'settings.schedule_cron_jobs'
  | 'reports.poster_overview'
  | 'reports.poster_targets'
  | 'reports.manifests'
  | 'reports.supplier'
  | 'reports.top_sales'
  | 'reports.sales_by_category'
  | 'reports.productivity'
  | 'reports.productivity_stats'
  | 'reports.event_logs'
  | 'reports.downloads.listings'
  | 'reports.downloads.orders'
  | 'reports.downloads.manifest_items'
  | 'reports.downloads.shipments'
  | 'reports.downloads.products'
  | 'misc.edit_notes'

export type HomepageValue =
  | 'AUTO'
  | 'ADMIN_DASHBOARD'
  | 'SUPPLIER_DASHBOARD'
  | 'POSTING_HISTORY'
  | 'ORDERS'
  | 'MANIFESTS'
  | 'PRODUCTS'
  | 'REPORTS'
  | 'NEW_SHIPMENT'
  | 'BOOKS'
export interface User {
  apiToken: string
  email: string
  employee: boolean
  id: number
  name: string
  onboarded: boolean
  role: string
  homepage?: HomepageValue
  supplierId: number
  username: string
  permissions: UserPermission[]
  lastLoginAt?: Date
  roleId: number
  passwordWarning?: string
}

export interface SelectOption {
  label: string
  value: number
}

export interface CachedSelectOptions {
  values: SelectOption[]
  error?: AxiosError
  loading?: boolean
}

export interface ProductMeta {
  page: number
  perPage: number
  sort: string
  createdAt: string[]
  query: string
  userId: number | null
  carrierId: number | null
  inventoryLocationId: number | null
  supplierId: number | null
  listingStrategyId: number | null
  categoryIds: number[] | null
  active: boolean | null
  draft: boolean | null
  hideFulfilled: boolean | null
  recycled: boolean | null
  excludePrinted: boolean | null
  onlyAuthenticated: boolean
  tags: string[]
  ignoreRecycledFilter: boolean
  total: number
}

export interface PaddingChoice {
  name: string
  value: string
  default: boolean
}

export interface InventoryLocationOption {
  label: string
  value: number
}

export interface PreloadedState {
  productMeta: ProductMeta
  current: {
    user: {
      permissions: string[]
    }
    organization: {
      connectedMarketAccounts: any[]
      settings: {
        watermarkImageSet: boolean
      }
      shippingSettings: {
        paddingChoices: PaddingChoice[]
      }
      productFormRequiredFields: string[]
    }
    qzConnection: {
      connected: boolean
    }
    currentHIDDevice: any | null
    inventoryLocationOptions: {
      loading: boolean
      values: InventoryLocationOption[]
    }
  }
}

export interface CurrentState {
  organization: Organization
  user: User
  featureFlags: { [key: string]: boolean }
  launchDarklySettings: LaunchDarklySettings
  preloadedState?: PreloadedState
  inventoryLocationOptions: CachedSelectOptions

  currentPrinter: string
  currentPrinterType: PrintLabelFormat
  testPrintType: TestPrintType
  currentHIDDevice: QZHIDDevice
  qzConnection: {
    loading: boolean
    connected: boolean
  }
  useFeature: { [key: string]: FeatureFlagState }
}

export type CurrentActionTypes =
  | SetCurrentHIDDeviceAction
  | SetCurrentPrinterAction
  | SetCurrentPrinterTypeAction
  | SetTestPrintTypeAction
  | EstablishQZConnectionAction
  | EstablishQZConnectionResultAction
  | SetProductLabelPrintSettingsAction
  | SetOrganizationSettingsAction
  | RequestSetOrganizationSettings
  | RequestSetOrganizationSettingsSuccess
  | RequestSetOrganizationSettingsError
  | RequestOrganizationSettings
  | RequestOrganizationSettingsSuccess
  | RequestOrganizationSettingsError
  | RequestInventoryLocations
  | RequestInventoryLocationsSuccess
  | RequestInventoryLocationsError
  | SetFeature
